//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { fillInMissingDates } from '@instagram-account/redux/engagement/instagramAccountEngagementUtils';
import {
  EngagementPostsAggregateStatValue,
  IEngagementPostsAggregateStats,
  IEngagementPostsTrendStats,
} from '@instagram-account/redux/engagement/posts/instagramAccountEngagementPostsModel';
import {
  AggregationStatType,
  PostsStatAggregationArray,
  PostsStatHistogramArray,
} from '@models/IAggregationResult';
import { reduceToRecord } from '@utils/reduceToRecord';

/**
 * Parses out and calculates the aggregate stats for the engagement posts stat bubbles.
 */
export function getEngagementPostAggregateStats(
  stats: PostsStatAggregationArray,
): IEngagementPostsAggregateStats {
  const statsMap = reduceToRecord(stats, (stat) => [stat.name, stat.value]);

  const comments = statsMap[AggregationStatType.Comments].sum;
  const views = statsMap[AggregationStatType.Views].sum;
  const likes = statsMap[AggregationStatType.Likes].sum;
  const avgReach = statsMap[AggregationStatType.Reach].avg;
  const totalReach = statsMap[AggregationStatType.Reach].sum;
  const totalFollowers = statsMap[AggregationStatType.Followers].sum;

  const totalInteractions = likes + comments;
  const engagementRate = totalReach ? totalInteractions / totalReach : 0;
  const followerEngagementRate = totalFollowers
    ? totalInteractions / totalFollowers
    : 0;

  return {
    [EngagementPostsAggregateStatValue.AverageReach]: avgReach,
    [EngagementPostsAggregateStatValue.TotalViews]: views,
    [EngagementPostsAggregateStatValue.TotalLikes]: likes,
    [EngagementPostsAggregateStatValue.TotalComments]: comments,
    [EngagementPostsAggregateStatValue.EngagementRate]: engagementRate,
    [EngagementPostsAggregateStatValue.FollowerEngagementRate]:
      followerEngagementRate,
  };
}

const DAY_FORMAT = 'YYYY-MM-DD';

/**
 * Parses out and calculates the stats per day for the engagement posts trend graph.
 */
export function getEngagementPostTrendStats(
  startDate: Date,
  endDate: Date,
  histogram: PostsStatHistogramArray,
): IEngagementPostsTrendStats {
  const sparseTrendStatsByDay: IEngagementPostsTrendStats = histogram.map(
    (day) => {
      const { count, date, stats } = day;
      const statsMap = reduceToRecord(stats, (stat) => [stat.name, stat.value]);

      const comments = statsMap[AggregationStatType.Comments].sum;
      const likes = statsMap[AggregationStatType.Likes].sum;
      const averageReach = statsMap[AggregationStatType.Reach].avg;
      const totalReach = statsMap[AggregationStatType.Reach].sum;
      const totalFollowers = statsMap[AggregationStatType.Followers].sum;

      const totalInteractions = likes + comments;
      const totalEngagementRate = totalReach
        ? totalInteractions / totalReach
        : 0;
      const totalFollowerEngagementRate = totalFollowers
        ? totalInteractions / totalFollowers
        : 0;

      return {
        totalEngagementRate,
        totalFollowerEngagementRate,
        averageReach,
        postCount: count,
        date: new Date(date),
        readableDate: moment(date).tz('America/Los_Angeles').format(DAY_FORMAT),
      };
    },
  );

  return fillInMissingDates(
    sparseTrendStatsByDay,
    startDate,
    endDate,
    (date: Date) => ({
      totalEngagementRate: 0,
      totalFollowerEngagementRate: 0,
      averageReach: 0,
      postCount: 0,
      date,
      readableDate: moment(date).tz('America/Los_Angeles').format(DAY_FORMAT),
    }),
  );
}
